/**
 * Mouse-drag rectangle-selection for the merged table's availability grid
 * (offer-unified-table, CU-86cbc3r8b) — the drag half of
 * `use-offer-table-multi-select.ts`'s multi-select seam; see that file's
 * own doc comment for why click and drag are split apart.
 */
import * as React from 'react';
import {
  computeMultiSelectRectangle,
  type AvailabilityGridData,
  type MultiCellSelection,
  type MultiSelectCellRef,
  type InventoryAvailabilityPeriod,
} from '@ooh/screens';

export interface UseMultiSelectDragOptions {
  canSelect: boolean;
  gridRows: { id: string; label: string }[];
  periods: InventoryAvailabilityPeriod[];
  grid: AvailabilityGridData;
  updateMultiSelectAnchor: (selection: MultiCellSelection | null) => void;
}

export interface UseMultiSelectDragResult {
  handleCellMouseDown: (rowId: string, period: InventoryAvailabilityPeriod) => void;
  handleCellMouseEnter: (rowId: string, period: InventoryAvailabilityPeriod) => void;
  handleCellMouseUp: (rowId: string, period: InventoryAvailabilityPeriod) => void;
}

export function useMultiSelectDrag({
  canSelect,
  gridRows,
  periods,
  grid,
  updateMultiSelectAnchor,
}: UseMultiSelectDragOptions): UseMultiSelectDragResult {
  const dragStartRef = React.useRef<MultiSelectCellRef | null>(null);
  const isDraggingRef = React.useRef(false);

  const handleCellMouseDown = React.useCallback(
    (rowId: string, period: InventoryAvailabilityPeriod) => {
      if (!canSelect) return;
      dragStartRef.current = { unitId: rowId, periodId: period.id };
      isDraggingRef.current = false;
    },
    [canSelect],
  );

  const handleCellMouseEnter = React.useCallback(
    (rowId: string, period: InventoryAvailabilityPeriod) => {
      if (!canSelect) return;
      const start = dragStartRef.current;
      if (!start || (start.unitId === rowId && start.periodId === period.id)) return;
      isDraggingRef.current = true;
      const cells = computeMultiSelectRectangle(gridRows, periods, grid, start, {
        unitId: rowId,
        periodId: period.id,
      });
      updateMultiSelectAnchor({ cells, anchor: { unitId: rowId, periodId: period.id } });
    },
    [canSelect, gridRows, periods, grid, updateMultiSelectAnchor],
  );

  const handleCellMouseUp = React.useCallback(
    (rowId: string, period: InventoryAvailabilityPeriod) => {
      if (!canSelect) return;
      const start = dragStartRef.current;
      if (isDraggingRef.current && start) {
        const cells = computeMultiSelectRectangle(gridRows, periods, grid, start, {
          unitId: rowId,
          periodId: period.id,
        });
        updateMultiSelectAnchor({ cells, anchor: { unitId: rowId, periodId: period.id } });
      }
      dragStartRef.current = null;
      isDraggingRef.current = false;
    },
    [canSelect, gridRows, periods, grid, updateMultiSelectAnchor],
  );

  return { handleCellMouseDown, handleCellMouseEnter, handleCellMouseUp };
}
